import { api, type SendConfig } from './api';
import {
  TaskWithDetails,
  TaskType,
  TaskStats,
  TaskHistory,
  TaskComment,
  TaskAttachment,
  PaginatedTasks,
  CreateTaskData,
  UpdateTaskData,
  UpdateTaskStatusData,
  AssignTaskData,
  EscalateTaskData,
  CreateCommentData,
} from '@/types/task';

export interface TaskListParams {
  page?: number;
  limit?: number;
  incident_id?: string;
  search?: string;
  status?: string;
  priority?: string;
  task_type_id?: string;
  assigned_esf_id?: string;
  /** Unified org tree filter (incident_org_nodes). Supersedes the legacy ICS-position filter (Phase 4). */
  org_node_id?: string;
  my_tasks?: boolean;
  tags?: string[];
}

/**
 * Stats accept the same filters as the list, minus pagination — the KPI strip must count the
 * whole filtered set, not just the page of rows the list happens to be showing.
 */
export type TaskStatsParams = Omit<TaskListParams, 'page' | 'limit'>;

function buildQueryString(params: Record<string, unknown>): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '' || value === false) continue;
    if (Array.isArray(value)) {
      qs.append(key, value.join(','));
    } else {
      qs.append(key, String(value));
    }
  }
  const str = qs.toString();
  return str ? `?${str}` : '';
}

export const taskService = {
  getTasks: (params: TaskListParams) =>
    api.get<PaginatedTasks>(`/tasks${buildQueryString(params as unknown as Record<string, unknown>)}`),

  getTaskById: (id: string) =>
    api.get<TaskWithDetails>(`/tasks/${id}`),

  getTaskStats: (params: TaskStatsParams = {}) =>
    api.get<TaskStats>(`/tasks/stats${buildQueryString(params as unknown as Record<string, unknown>)}`),

  getTaskTypes: () =>
    api.get<TaskType[]>('/tasks/types'),

  // `config` pins the tenant of a stored offline row (offline/task.service.ts sync).
  createTask: (data: CreateTaskData, config?: SendConfig) =>
    api.post<TaskWithDetails>('/tasks', data, config),

  updateTask: (id: string, data: UpdateTaskData, config?: SendConfig) =>
    api.patch<TaskWithDetails>(`/tasks/${id}`, data, config),

  deleteTask: (id: string, config?: SendConfig) =>
    api.delete(`/tasks/${id}`, undefined, config),

  updateTaskStatus: (id: string, data: UpdateTaskStatusData, config?: SendConfig) =>
    api.patch(`/tasks/${id}/status`, data, config),

  assignTask: (id: string, data: AssignTaskData, config?: SendConfig) =>
    api.post(`/tasks/${id}/assign`, data, config),

  escalateTask: (id: string, data: EscalateTaskData) =>
    api.post(`/tasks/${id}/escalate`, data),

  exportTasksCsv: (params: Record<string, unknown>) =>
    api.getBlob(`/tasks/export${buildQueryString(params)}`),

  getDistinctTags: (incidentId?: string) =>
    api.get<string[]>(`/tasks/tags${buildQueryString({ incident_id: incidentId })}`),

  unassignTask: (id: string, reason?: string) =>
    api.post(`/tasks/${id}/unassign`, { reason: reason || 'Unassigned by user' }),

  getComments: (taskId: string, params: { limit?: number; after?: string } = {}) =>
    api.get<{ comments: TaskComment[]; total: number; limit: number; has_more: boolean; next_cursor: string | null }>(
      `/tasks/${taskId}/comments${buildQueryString({ limit: params.limit ?? 10, after: params.after })}`
    ),

  addComment: (taskId: string, data: CreateCommentData) =>
    api.post(`/tasks/${taskId}/comments`, data),

  getHistory: (taskId: string) =>
    api.get<TaskHistory[]>(`/tasks/${taskId}/history`),

  getChildTasks: (parentTaskId: string) =>
    api.get<TaskWithDetails[]>(`/tasks/${parentTaskId}/children`),

  // Attachments
  getAttachments: (taskId: string) =>
    api.get<(TaskAttachment & { url: string })[]>(`/tasks/${taskId}/attachments`),

  uploadAttachment: (taskId: string, file: File) => {
    const formData = new FormData();
    formData.append('files', file);
    return api.upload<TaskAttachment[]>(`/tasks/${taskId}/attachments`, formData);
  },

  deleteAttachment: (taskId: string, attachmentId: string) =>
    api.delete(`/tasks/${taskId}/attachments/${attachmentId}`),

  // Bulk operations (single API call each)
  bulkDelete: (ids: string[]) =>
    api.post<{ deleted: number }>('/tasks/bulk/delete', { ids }),

  // NOTE: the backend skips tasks whose status transition is invalid or that are
  // on safety hold, so `updated` can be less than `ids.length`. Callers must read
  // `skipped` — otherwise a partly-applied bulk change reads as a full success.
  bulkUpdateStatus: (ids: string[], status: string, reason?: string) =>
    api.post<{ updated: number; skipped?: number }>('/tasks/bulk/status', { ids, status, reason: reason || undefined }),

  bulkUpdatePriority: (ids: string[], priority: string) =>
    api.post<{ updated: number }>('/tasks/bulk/priority', { ids, priority }),

  bulkAssign: (ids: string[], assignedTo: string) =>
    api.post<{ updated: number }>('/tasks/bulk/assign', { ids, assigned_to: assignedTo }),
};
